import PropTypes from 'prop-types';
import React from 'react';
import api from '../../../Api';
import makeConnectTo from '../../util/makeConnectTo';
import { connectToTitle } from '../../Title/containers/TitleDataStore';
import combineConnectors from '../../util/combineConnectors';
import { connectToConversations } from './conversationsDataStore';

const CommentsContext = React.createContext();

const name = 'commentsDataStore';

export const useComments = () => React.useContext(CommentsContext);

export const connectToComments = makeConnectTo(CommentsContext, name);

export class CommentsDataStore extends React.Component {
	static propTypes = {
		children: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
	};

	state = {
		topicId: 0,
		comments: [],
		isLoading: true,
		scrollOnUpdate: true,
	};

	render() {
		const commentsDataStore = this.getStore();
		return (
			<CommentsContext.Provider value={commentsDataStore}>
				{typeof this.props.children === 'function'
					? this.props.children(commentsDataStore)
					: this.props.children}
			</CommentsContext.Provider>
		);
	}

	componentDidMount() {
		this.fetchComments();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.id !== this.props.id) {
			this.refreshComments();
		}
	}

	componentWillUnmount() {
		if (this.commentsFetch && this.commentsFetch.cancel) {
			this.commentsFetch.cancel();
		}
	}

	getStore = (state = this.state) => ({
		state,
		refresh: noLoading => this.refreshComments(noLoading),
		toggleBadge: (commentId, badgeType) =>
			this.toggleBadge(commentId, badgeType),
		createNewComment: (commentDTO, isReply) =>
			this.createNewComment(commentDTO, isReply),
		updateComment: commentDTO => this.updateComment(commentDTO),
		changeConversationStatus: (communityId, status) =>
			this.changeConversationStatus(communityId, status),
		getScrollOnUpdate: () => this.getScrollOnUpdate(),
		changeCommentStatus: (commentId, status) =>
			this.changeCommentStatus(commentId, status),
	});

	refreshComments = noLoading => {
		if (!noLoading) {
			this.setState({
				isLoading: true,
			});
		}
		this.fetchComments();
		return this.commentsFetch.promise;
	};

	fetchComments = () => {
		const { type, id } = this.props;
		const request = api.cancelableGet(
			`/api/v1/conversations/comments/${type.key}/${id}`,
		);
		request.promise.then(this.cacheApiResponse);
		this.commentsFetch = request;
	};

	cacheApiResponse = apiResponse => {
		const { type, id } = this.props;
		const threads = apiResponse && this.getThreads(apiResponse);
		this.setState({
			threads: threads.sort(
				(a, b) =>
					new Date(a.mostRecentComment) -
					new Date(b.mostRecentComment),
			),
			type: type,
			id: id,
			isLoading: false,
		});
	};

	getThreads = apiResponse => {
		const {
			comments,
			threads,
			userAvatarsByAppUserId,
			usersById,
			organizationsById,
			badges,
		} = apiResponse;
		return threads.map(thread => {
			return {
				...thread,
				comments: this.getComments(
					thread.parentCommentId,
					comments,
					userAvatarsByAppUserId,
					usersById,
					organizationsById,
					badges,
				),
			};
		});
	};

	getComments = (
		parentCommentId,
		comments,
		userAvatarsByAppUserId,
		usersById,
		organizationsById,
		badges,
	) => {
		const threadComments = comments
			.filter(com => com.thread == parentCommentId)
			.sort((a, b) => new Date(a.updatedDate) - new Date(b.updatedDate));
		const badgesWithNames =
			badges && this.getBadgesWithNames(badges, userAvatarsByAppUserId);
		return threadComments.map(comment => {
			return {
				...comment,
				badges:
					badgesWithNames &&
					badgesWithNames.filter(bad => bad.commentId == comment.id),
				userAvatar: userAvatarsByAppUserId[comment.appUserId],
				orgName:
					usersById[comment.appUserId] &&
					usersById[comment.appUserId].orgID &&
					organizationsById[
						usersById[comment.appUserId].orgID.toLowerCase()
					] &&
					organizationsById[
						usersById[comment.appUserId].orgID.toLowerCase()
					].name,
			};
		});
	};

	getBadgesWithNames = (badges, userAvatarsByAppUserId) => {
		return badges.map(badge => {
			return {
				...badge,
				userName: userAvatarsByAppUserId[badge.appUserId].displayName,
			};
		});
	};

	getOwnerInfo = (appUserId, userAvatar, organization) => {
		var owner = {};
		owner.userAvatar = userAvatar && userAvatar;
		owner.organization = organization.name;
		owner.appUserId = appUserId;
		return owner;
	};

	toggleBadge = (commentId, badgeType) => {
		var badgeDTO = {
			commentId: commentId,
			type: badgeType,
		};
		api.put(`/api/v1/conversations/badges`, badgeDTO).then(() => {
			this.setState({ scrollOnUpdate: false });
			this.refreshComments(true);
		});
	};

	getScrollOnUpdate = () => {
		return this.state.scrollOnUpdate;
	};

	createNewComment = (commentDTO, isReply) => {
		if (!isReply) {
			const { type, id } = this.props;
			commentDTO.conversationType = type.key;
			commentDTO.conversationId = id;
			commentDTO.status = 0;
		}
		api.post(`/api/v1/conversations/comments`, commentDTO).then(() => {
			this.setState({ scrollOnUpdate: true });
			this.refreshComments(true);
			this.props.titleDataStore &&
				this.props.titleDataStore.refreshConversations();
		});
	};

	updateComment = commentDTO => {
		api.put(`/api/v1/conversations/comments`, commentDTO).then(() => {
			this.setState({ scrollOnUpdate: true });
			this.refreshComments(true);
		});
	};

	changeCommentStatus = (commentId, status) => {
		api.del(`/api/v1/conversations/comments/${commentId}/${status}`).then(
			() => {
				this.setState({ scrollOnUpdate: false });
				this.refreshComments(true);
			},
		);
	};

	changeConversationStatus = (communityId, status) => {
		const { type, id } = this.props;
		var conversationDTO = {};
		conversationDTO.conversationType = type.key;
		conversationDTO.conversationId = id;
		conversationDTO.communityId = communityId;
		conversationDTO.status = status;
		api.put(`/api/v1/conversations/status`, conversationDTO).then(() => {
			this.refreshComments(true);
		});
	};
}

const connectors = [connectToTitle, connectToConversations];

export default combineConnectors(connectors)(CommentsDataStore);
